import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { searchApi, analyticsApi } from '../api/search'
import { Network, Download } from 'lucide-react'
import { downloadCSV } from '../utils/csv'
import ResultsTable from '../components/ResultsTable'
import AnalyticsPage from './AnalyticsPage'
import NetworkVisualization from '../components/NetworkVisualization'
import SearchSummaryBox from '../components/SearchSummaryBox'
import { useSearchFilters } from '../contexts/SearchFiltersContext'

export default function SearchPage() {
  const [searchParams] = useSearchParams()
  const [searchId, setSearchId] = useState<string | null>(searchParams.get('search_id'))
  const [activeMainTab, setActiveMainTab] = useState<'results' | 'analytics' | 'network'>('results')
  const prevUrlSearchIdRef = useRef<string | null>(null)
  const enrichTriggeredRef = useRef<Set<string>>(new Set())
  const queryClient = useQueryClient()
  const { filteredPmids, iciteFetchTime, setIciteFetchTime, datasetAnalysis, datasetTopicNames, enhancedTopicAnnotations, currentAnalysis, currentTopicNames, activeFilters } = useSearchFilters()

  // Update searchId when URL changes and auto-switch to results tab
  useEffect(() => {
    const urlSearchId = searchParams.get('search_id')
    if (urlSearchId && urlSearchId !== prevUrlSearchIdRef.current) {
      prevUrlSearchIdRef.current = urlSearchId
      setSearchId(urlSearchId)
      setActiveMainTab('results')
    }
  }, [searchParams])

  const { data: searchResult } = useQuery({
    queryKey: ['search', searchId],
    queryFn: () => searchApi.getSearch(searchId!),
    enabled: !!searchId,
    staleTime: 5 * 60 * 1000, // 5 minutes - keep data fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    placeholderData: (previousData) => previousData, // keep showing old data while loading
    refetchInterval: (query) => {
      if (query?.state?.data?.search?.status === 'running') return 2000
      return false
    },
  })

  // Server-side pagination: searchResult now ships pmids + counts only.
  // The actual rows are fetched by ResultsTable via the pageFetcher below.
  const allPmids: string[] = searchResult?.pmids || []
  const totalCount = searchResult?.total ?? allPmids.length
  const enrichedCount = searchResult?.enriched_count ?? 0

  const filteredPmidsArr = filteredPmids.size > 0
    ? allPmids.filter(p => filteredPmids.has(p))
    : null
  const displayCount = filteredPmidsArr ? filteredPmidsArr.length : totalCount
  const hasResults = searchId && searchResult && searchResult.search.status === 'complete' && displayCount > 0

  // Stable refresh key for the table page fetcher: changes whenever the
  // filter set changes so ResultsTable resets to page 0 and refetches.
  const pageFetcherRefreshKey = `${searchId || ''}|${filteredPmidsArr ? filteredPmidsArr.join(',').slice(0, 256) + ':' + filteredPmidsArr.length : 'all'}`

  const pageFetcher = searchId
    ? {
        refreshKey: pageFetcherRefreshKey,
        fetch: async (opts: { offset: number; limit: number; sort_by?: string; sort_dir?: 'asc' | 'desc' }) => {
          return await searchApi.getSearchRows(searchId, {
            offset: opts.offset,
            limit: opts.limit,
            sort_by: opts.sort_by,
            sort_dir: opts.sort_dir,
            pmids: filteredPmidsArr || undefined,
          })
        },
      }
    : undefined

  // Auto-enrich with iCite citations once the search completes.
  // Fires once per search_id per session; skips searches that already have
  // citation data (e.g. revisiting an old completed search).
  const enrichMutation = useMutation({
    mutationFn: (id: string) => analyticsApi.fetchCitations(id),
    onSuccess: (_data, id) => {
      setIciteFetchTime(Date.now())
      queryClient.invalidateQueries({ queryKey: ['search', id] })
      queryClient.invalidateQueries({ queryKey: ['analytics-overview', id] })
      queryClient.invalidateQueries({ queryKey: ['analytics-trends', id] })
      queryClient.invalidateQueries({ queryKey: ['analytics-keywords', id] })
      queryClient.invalidateQueries({ queryKey: ['analytics-journals', id] })
    },
  })

  useEffect(() => {
    if (!searchId) return
    if (searchResult?.search.status !== 'complete') return
    if (totalCount === 0) return
    if (enrichTriggeredRef.current.has(searchId)) return
    // The backend's enriched_count counts rows whose `citations` column is
    // non-null. Treat the search as "already enriched" only if a clear
    // majority of its articles already carry citation data.
    if (enrichedCount / totalCount >= 0.5) return
    enrichTriggeredRef.current.add(searchId)
    enrichMutation.mutate(searchId)
  }, [searchId, searchResult?.search.status, totalCount, enrichedCount])

  // Build PMID → topic mapping from loaded dataset analysis or current analysis
  const activeAnalysis = datasetAnalysis || currentAnalysis
  const activeTopicNames = Object.keys(datasetTopicNames).length > 0 ? datasetTopicNames : currentTopicNames
  
  console.log('DEBUG SearchPage:', {
    hasDatasetAnalysis: !!datasetAnalysis,
    hasCurrentAnalysis: !!currentAnalysis,
    datasetTopicNamesKeys: Object.keys(datasetTopicNames),
    currentTopicNamesKeys: Object.keys(currentTopicNames),
    activeTopicNamesKeys: Object.keys(activeTopicNames),
    enhancedTopicAnnotations,
    documentCount: activeAnalysis?.documents?.length
  })
  
  const topicMapping = (() => {
    if (!enhancedTopicAnnotations || !activeAnalysis?.documents) return undefined
    const mapping: Record<string, number> = {}
    activeAnalysis.documents.forEach((doc: any) => {
      if (doc.pmid != null && doc.dominant_topic != null) {
        mapping[doc.pmid] = doc.dominant_topic
      } else if (doc.pmid != null && doc.topic != null) {
        mapping[doc.pmid] = doc.topic
      }
    })
    console.log('DEBUG topicMapping built:', Object.keys(mapping).length, 'docs, sample:', Object.entries(mapping).slice(0, 3))
    return mapping
  })()

  return (
    <div style={{ minHeight: 'calc(100vh - 140px)' }}>
      {/* MAIN CONTENT */}
      <div className="min-w-0">
        {!searchId ? (
          <div className="flex items-center justify-center h-96 bg-white rounded-lg shadow-sm border">
            <div className="text-center text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-lg font-medium">Enter a search query to get started</p>
              <p className="text-sm mt-1">Move mouse to left edge to open search panel</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border flex flex-col" style={{ minHeight: 'calc(100vh - 140px)' }}>
            {/* Tabs Header */}
            <div className="border-b border-gray-200 px-6 pt-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Search Results</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Status:{' '}
                    <span className={`font-medium ${
                      searchResult?.search.status === 'complete' ? 'text-green-600' : 
                      searchResult?.search.status === 'error' ? 'text-red-600' : 'text-blue-600'
                    }`}>
                      {searchResult?.search.status || 'pending'}
                    </span>
                    {' | '}
                    Found: {searchResult?.search.total_count?.toLocaleString() || 0}
                    {' | '}
                    Showing: {displayCount.toLocaleString()}
                    {filteredPmids.size > 0 && ` (filtered from ${totalCount.toLocaleString()})`}
                  </p>
                </div>
              </div>

              {enrichMutation.isPending && (
                <div className="mb-3">
                  <div className="flex items-center gap-2 text-xs text-blue-700 mb-1">
                    <span className="animate-spin inline-block h-3 w-3 border-b-2 border-blue-600 rounded-full" />
                    Fetching citation metrics for {totalCount.toLocaleString()} articles…
                  </div>
                  <div className="h-1 bg-blue-100 overflow-hidden rounded-full">
                    <div className="h-full bg-blue-500 rounded-full animate-pulse" />
                  </div>
                </div>
              )}

              <nav className="flex gap-6">
                <button
                  onClick={() => setActiveMainTab('results')}
                  className={`pb-3 px-1 border-b-2 text-sm font-medium ${
                    activeMainTab === 'results'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Results
                </button>
                <button
                  onClick={() => setActiveMainTab('analytics')}
                  disabled={!hasResults}
                  className={`pb-3 px-1 border-b-2 text-sm font-medium ${
                    activeMainTab === 'analytics'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 disabled:text-gray-300'
                  }`}
                >
                  Analytics
                </button>
                <button
                  onClick={() => setActiveMainTab('network')}
                  disabled={!hasResults}
                  className={`pb-3 px-1 border-b-2 text-sm font-medium flex items-center gap-1 ${
                    activeMainTab === 'network'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 disabled:text-gray-300'
                  }`}
                >
                  <Network className="w-4 h-4" />
                  Network
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="flex-1 p-6 overflow-auto">
              {searchResult?.search.status === 'error' && (
                <div className="p-4 bg-red-50 text-red-700 rounded-md mb-4">
                  Error: {searchResult.search.error_message}
                </div>
              )}

              {activeMainTab === 'results' && (
                <>
                  {searchResult?.search && (
                    <SearchSummaryBox
                      query={searchResult.search.query}
                      queryBuilt={searchResult.search.query_built}
                      pubmedTotal={searchResult.search.total_count ?? 0}
                      maxResults={searchResult.search.max_results}
                      liteMode={searchResult.search.lite_mode}
                      filters={activeFilters as any}
                    />
                  )}
                  {displayCount > 0 ? (
                    <>
                      <div className="flex justify-end mb-4">
                        <button
                          onClick={async () => {
                            if (!searchId) return
                            // Pull all rows in 1k-row pages, then write the CSV in-browser.
                            const all: any[] = []
                            const PAGE = 1000
                            let offset = 0
                            while (true) {
                              const res = await searchApi.getSearchRows(searchId, {
                                offset, limit: PAGE,
                                pmids: filteredPmidsArr || undefined,
                              })
                              all.push(...res.rows)
                              if (res.rows.length < PAGE) break
                              offset += PAGE
                              if (all.length >= res.total) break
                            }
                            downloadCSV(all, `search_results_${searchId}_${new Date().toISOString().split('T')[0]}.csv`)
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm"
                        >
                          <Download className="w-4 h-4" />
                          Download CSV
                        </button>
                      </div>
                      <ResultsTable
                        articles={[]}
                        pageFetcher={pageFetcher}
                        topicMapping={enhancedTopicAnnotations ? topicMapping : undefined}
                        topicNames={enhancedTopicAnnotations ? activeTopicNames : undefined}
                        searchId={searchId}
                      />
                    </>
                  ) : searchResult?.search.status === 'complete' ? (
                    <div className="text-center py-12 text-gray-500">
                      <p>No articles match the current filters.</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 w-full max-w-md mx-auto">
                      <div className="flex items-center mb-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                        <span className="ml-3 text-gray-600">
                          {(() => {
                            const status = searchResult?.search.status
                            const fetched = searchResult?.search.progress_fetched || 0
                            const total = searchResult?.search.progress_total || 0
                            // Initial mount (e.g. after page refresh) — query hasn't resolved yet.
                            if (!searchResult) return 'Loading articles from server…'
                            if (status === 'complete') return `Loading ${(searchResult.articles?.length || 0).toLocaleString()} articles…`
                            if (total === 0) return 'Starting search…'
                            if (fetched < total) return `Retrieving ${fetched.toLocaleString()} / ${total.toLocaleString()} articles…`
                            return `Saving ${total.toLocaleString()} articles to database…`
                          })()}
                        </span>
                      </div>
                      {(searchResult?.search.progress_total || 0) > 0 && (() => {
                        const total = searchResult?.search.progress_total || 1
                        const local = searchResult?.search.progress_local || 0
                        const fetched = searchResult?.search.progress_fetched || 0
                        const downloaded = Math.max(0, fetched - local)
                        const localPct = (local / total) * 100
                        const downloadedPct = (downloaded / total) * 100
                        const overallPct = Math.min(100, Math.round((fetched / total) * 100))
                        return (
                          <div className="w-full">
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
                              <div
                                className="h-full bg-green-500 transition-all duration-500"
                                style={{ width: `${localPct}%` }}
                                title="From local cache"
                              />
                              <div
                                className="h-full bg-blue-500 transition-all duration-500"
                                style={{ width: `${downloadedPct}%` }}
                                title="Downloaded from PubMed"
                              />
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500 mt-1.5">
                              <div className="flex gap-3">
                                <span className="flex items-center gap-1">
                                  <span className="inline-block w-2 h-2 bg-green-500 rounded-sm" />
                                  Cached: {local.toLocaleString()}
                                </span>
                                <span className="flex items-center gap-1">
                                  <span className="inline-block w-2 h-2 bg-blue-500 rounded-sm" />
                                  Downloaded: {downloaded.toLocaleString()}
                                </span>
                              </div>
                              <span>{overallPct}%</span>
                            </div>
                          </div>
                        )
                      })()}
                    </div>
                  )}
                </>
              )}

              {activeMainTab === 'analytics' && hasResults && (
                <AnalyticsPage searchId={searchId} iciteFetchTime={iciteFetchTime} />
              )}

              {activeMainTab === 'network' && hasResults && (
                <NetworkVisualization searchId={searchId} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
